import React, { useEffect, useState } from "react";
import CustomCheckBox from "../../../../components/customCheckBox/customCheckBox";
import { Question } from "../../types";
import RecursiveQuestion from "../recursiveQuestion/recursiveQuestion";
import "./multiCorrectQuestion.scss";

export interface MultiCorrectQuestionProps {
  question: Question;
  onAnswerChange?: (questionId: string, answerIds: string[]) => void;
  disabled?: boolean;
  currentAnswers?: { id: string; answer: any }[];
  questionNumber?: string;
}

const MultiCorrectQuestion: React.FC<MultiCorrectQuestionProps> = ({
  question,
  onAnswerChange,
  disabled = false,
  currentAnswers = [],
  questionNumber = "1",
}) => {
  const [selectedAnswers, setSelectedAnswers] = useState<string[]>([]);

  useEffect(() => {
    if (question.answer && Array.isArray(question.answer)) {
      setSelectedAnswers(question.answer.map((answer) => answer.id) ?? []);
    } else if (
      question.answer &&
      typeof question.answer === "object" &&
      Array.isArray(question.answer) &&
      (question.answer as string)
    ) {
      setSelectedAnswers([question.answer as string]);
    }
  }, [question.answer]);

  const handleAnswerChange = (optionId: string, isChecked: boolean) => {
    setSelectedAnswers((prev) => {
      let newAnswers;
      if (isChecked) {
        newAnswers = prev.includes(optionId) ? prev : [...prev, optionId];
      } else {
        newAnswers = prev.filter((id) => id !== optionId);
      }

      if (onAnswerChange) {
        onAnswerChange(question.id, newAnswers);
      }

      return newAnswers;
    });
  };

  const getChildQuestionsForOption = (optionId: string) => {
    return question.children?.[optionId] || [];
  };

  const isOptionSelected = (optionId: string) => {
    return selectedAnswers.includes(optionId);
  };

  return (
    <div className="multi-correct-question--container">
      <div className="multi-correct-question--question">
        <span className="text-primaryBlue-1 mr-2 font-semibold">
          {questionNumber}.
        </span>
        {question.question}
        {question.required && <span className="required-asterisk">*</span>}
      </div>

      <div className="multi-correct-question--options text-sm">
        {question.options.map((option) => {
          const childQuestions = getChildQuestionsForOption(option.id);
          const isSelected = isOptionSelected(option.id);

          return (
            <div
              key={option.id}
              className="multi-correct-question--option-container"
            >
              <CustomCheckBox
                id={`${question.id}-${option.id}`}
                label={option.text}
                isChecked={isSelected}
                onCheckBoxValueChange={(data) =>
                  handleAnswerChange(option.id, data.value)
                }
                isDisabled={disabled}
                className="multi-correct-question--checkbox-option"
              />

              {isSelected && childQuestions.length > 0 && (
                <div className="multi-correct-question--child-questions ml-6 mt-2 space-y-4">
                  {childQuestions.map((childQuestion, childIndex) => (
                    <RecursiveQuestion
                      key={childQuestion.id}
                      question={childQuestion}
                      onAnswerChange={onAnswerChange || (() => {})}
                      currentAnswers={currentAnswers}
                      depth={1}
                      questionNumber={`${questionNumber}.${childIndex + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MultiCorrectQuestion;
